import { useEffect, useState } from "react";
import api from "@/lib/api";
import AgentCard from "@/components/AgentCard";
import AgentActivityFeed from "@/components/AgentActivityFeed";
import StatusPill from "@/components/StatusPill";
import PanelState, { extractApiError } from "@/components/PanelState";
import { X, Sparkles } from "lucide-react";

// Not-yet-built agents from the roadmap. Kept in the roster page as informational
// grayed-out cards so the operator sees what's coming without confusing them for live agents.
const COMING_SOON_AGENTS = [
  { id: "closer",         name: "The Closer",           function: "Cancellation Recovery",       description: "Wins back cancellations by triggering targeted outreach before the job is truly lost." },
  { id: "recruiter",      name: "The Recruiter",        function: "Talent Pipeline",             description: "Sourcing, screening, and shortlist automation for the tech and CSR pipeline." },
  { id: "scorekeeper",    name: "The Scorekeeper",      function: "Weekly KPI Report",           description: "Compiles the weekly performance summary and emails it to leadership on Monday morning." },
  { id: "coach_assistant", name: "The Coach's Assistant", function: "Call Coaching (Rilla)",     description: "Rilla-powered call reviews, scored per CSR, with coaching cards for the manager." },
];

export default function AgentRoster() {
  const [agents, setAgents] = useState([]);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [selected, setSelected] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsState, setRunsState] = useState({ status: "loading", error: null });

  const load = async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const { data } = await api.get("/agents");
      setAgents((data || []).filter((a) => a.id !== "raymond"));
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setAgents([]);
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  };
  useEffect(() => { load(); }, []);

  const open = async (agent) => {
    setSelected(agent);
    setRuns([]);
    setRunsState({ status: "loading", error: null });
    try {
      const { data } = await api.get(`/agents/${agent.id}/runs`);
      setRuns(data || []);
      setRunsState({ status: "ok", error: null });
    } catch (err) {
      setRunsState({ status: "error", error: extractApiError(err) });
    }
  };

  return (
    <div data-testid="agents-page" className="space-y-5">
      <header>
        <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
          Tenant-scoped
        </div>
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">Agent Roster</h1>
        <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
          Every agent, its cadence, its latest run — same code, tenant-specific data.
        </p>
      </header>

      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={agents.length === 0}
        onRetry={load}
        emptyTitle="No agents configured for this tenant"
        emptyBody={<>The <code className="font-mono-data">agent_definitions</code> catalog returned zero rows.</>}
        testIdPrefix="agents"
      />

      {loadState.status === "ok" && agents.length > 0 && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-6 min-w-0">
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display font-semibold text-[16px] text-[#0e1420]">Active Agents</h2>
                <span className="text-[11px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
                  {agents.length} live
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                {agents.map((a) => (
                  <AgentCard key={a.id} agent={a} onClick={() => open(a)} />
                ))}
              </div>
            </section>

            <section data-testid="agents-coming-soon-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} strokeWidth={1.8} className="text-[#FFC72C]" />
                <h2 className="font-display font-semibold text-[16px] text-[#0e1420]">Coming Soon</h2>
                <span className="text-[10.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
                  · Not yet built — roadmap
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                {COMING_SOON_AGENTS.map((a) => (
                  <AgentCard key={a.id} agent={a} comingSoon />
                ))}
              </div>
            </section>
          </div>

          <aside className="xl:sticky xl:top-4 self-start">
            <AgentActivityFeed />
          </aside>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setSelected(null)}
          data-testid="agent-detail-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-[640px] max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-[#eef1f6] flex items-start gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-display font-bold text-[19px]"
                style={{ background: selected.avatar_color }}
              >
                {selected.initial}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-[20px] text-[#0e1420]">{selected.name}</div>
                  <span className="text-[10.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
                    {selected.agent_code}
                  </span>
                </div>
                <div className="text-[13px] text-[color:var(--cc-ink-muted)] mt-1">{selected.function}</div>
                <div className="mt-2 flex items-center gap-3">
                  <StatusPill status={selected.status} />
                  <span className="text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
                    Cadence: {selected.cadence}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 rounded-md hover:bg-[#f5f6f8]"
                data-testid="agent-modal-close"
              >
                <X size={18} strokeWidth={1.6} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="text-[11px] uppercase tracking-[.14em] text-[color:var(--cc-ink-muted)] font-semibold mb-3">
                Recent runs
              </div>
              <PanelState
                status={runsState.status}
                error={runsState.error}
                isEmpty={runs.length === 0}
                onRetry={() => open(selected)}
                emptyTitle="No runs yet"
                emptyBody={<>This agent hasn&apos;t executed any runs for this tenant.</>}
                testIdPrefix="agent-runs"
              />
              {runsState.status === "ok" && runs.length > 0 && (
                <div className="space-y-2">
                  {runs.map((r) => (
                    <div key={r.id} className="flex items-start gap-3 py-2 border-b border-[#eef1f6] last:border-b-0" data-testid={`agent-run-${r.id}`}>
                      <StatusPill status={r.status} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-[#0e1420]">{r.summary || "—"}</div>
                        <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5">
                          {new Date(r.started_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
